
import Cookies from 'js-cookie'; // npm install js-cookie
import { logout } from './auth';

const BASE_URL = process.env.NEXT_PUBLIC_API_URL || '/api/proxy/';

// Global map to store ongoing requests for deduplication
const pendingRequests = new Map();
// Global map to store cached responses
const cachedResponses = new Map();

async function fetcher(endpoint, { method, body, mode, skipAuthGuard, ...customConfig } = {}) {
    const token = Cookies.get('token');

    // 1. Clean URL concatenation
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
    const url = `${BASE_URL}${cleanEndpoint}`;

    const headers = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        //...(mode === 'cors' && { 'Access-Control-Allow-Origin': '*' }),
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...customConfig.headers, // Allow overriding headers
    };

    const config = {
        method: method || (body ? 'POST' : 'GET'),
        ...customConfig,
        headers,
    };

    if (body) config.body = JSON.stringify(body);

    // Deduplication logic for GET or specific POST requests
    const isCacheable = (config.method === 'GET' && !endpoint.includes('logout')) || 
                        (config.method === 'POST' && (endpoint.includes('view_home_cities') || endpoint.includes('master_localities')));
    
    // Create a deterministic cache key
    let cacheKeyStr = config.body || '';
    if (typeof config.body === 'string') {
        try {
            // Sort object keys so the cache key is identical regardless of JSON key order
            const parsed = JSON.parse(config.body);
            cacheKeyStr = JSON.stringify(parsed, Object.keys(parsed).sort());
        } catch(e) {}
    }
    const requestKey = isCacheable ? `${config.method}:${url}:${cacheKeyStr}` : null;

    if (isCacheable) {
        if (cachedResponses.has(requestKey)) {
            const { data, timestamp } = cachedResponses.get(requestKey);
            // 15 minute cache TTL for dropdown data
            if (Date.now() - timestamp < 15 * 60 * 1000) {
                console.log('Returning from cache API Request:', requestKey);
                // Return a deep copy to prevent mutation bugs by components
                return Promise.resolve(JSON.parse(JSON.stringify(data)));
            } else {
                cachedResponses.delete(requestKey);
            }
        } else if (typeof window !== 'undefined') {
            try {
                const localDataStr = localStorage.getItem(requestKey);
                if (localDataStr) {
                    const { data, timestamp } = JSON.parse(localDataStr);
                    if (Date.now() - timestamp < 15 * 60 * 1000) {
                        console.log('Returning from localStorage API Request:', requestKey);
                        cachedResponses.set(requestKey, { data, timestamp });
                        return Promise.resolve(JSON.parse(JSON.stringify(data)));
                    } else {
                        localStorage.removeItem(requestKey);
                    }
                }
            } catch (e) {
                // Ignore storage errors
            }
        }

        if (pendingRequests.has(requestKey)) {
            console.log('Deduplicating API Request:', requestKey);
            return pendingRequests.get(requestKey);
        }
    }

    console.log('API Request:', { url, method: config.method, headers: config.headers, body: body ? 'Body Present' : 'No Body' });

    const MAX_RETRIES = 3;
    const fetchPromise = (async () => {
        for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
            try {
                const response = await fetch(url, config);

                // Retry on 429 Too Many Requests with exponential backoff
                if (response.status === 429 && attempt < MAX_RETRIES) {
                    const delay = Math.pow(2, attempt) * 1000; // 1s, 2s, 4s
                    console.warn(`API 429 rate-limited, retrying in ${delay}ms (attempt ${attempt + 1}/${MAX_RETRIES})...`);
                    await new Promise(resolve => setTimeout(resolve, delay));
                    continue;
                }

                // 2. Global Auth Guard
                if (response.status === 401 && !skipAuthGuard) {
                    console.warn('API 401 Unauthorized, logging out...');
                    logout();
                    const authError = new Error('Session expired');
                    authError.status = 401;
                    return Promise.reject(authError);
                }

                // 3. Safe JSON Parsing
                const contentType = response.headers.get('content-type');
                const isJson = contentType && contentType.includes('application/json');

                let data;
                let errorText = null;
                if (isJson) {
                    data = await response.json();
                } else {
                    const textContent = await response.text();
                    errorText = textContent;
                    // Try to parse textContent as JSON just in case it's JSON but header is wrong
                    try {
                        if (textContent) data = JSON.parse(textContent);
                    } catch (e) {
                        // Not JSON
                    }
                }

                if (response.ok) {
                    if (isCacheable && data) {
                        const cachePayload = { data: JSON.parse(JSON.stringify(data)), timestamp: Date.now() };
                        // Store a deep copy in cache
                        cachedResponses.set(requestKey, cachePayload);
                        if (typeof window !== 'undefined') {
                            try {
                                localStorage.setItem(requestKey, JSON.stringify(cachePayload));
                            } catch (e) {
                                // Ignore storage errors
                            }
                        }
                    }
                    return data;
                }

                // 4. Structured Error Rejection
                const errorMsg = (typeof data === 'object' && data !== null)
                    ? (data.msg || data.error || data.message || response.statusText || `Request failed with status ${response.status}`)
                    : (data || response.statusText || `Request failed with status ${response.status}`);
                const apiError = new Error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
                if (typeof data === 'object' && data !== null) {
                    Object.assign(apiError, data);
                }
                apiError.status = response.status;
                apiError.statusText = response.statusText;
                apiError.details = errorText;
                return Promise.reject(apiError);

            } catch (error) {
                // Retry network errors too (but not on last attempt)
                if (attempt < MAX_RETRIES) {
                    const delay = Math.pow(2, attempt) * 1000;
                    console.warn(`API network error, retrying in ${delay}ms (attempt ${attempt + 1}/${MAX_RETRIES})...`);
                    await new Promise(resolve => setTimeout(resolve, delay));
                    continue;
                }
                return Promise.reject(error);
            } finally {
                if (attempt === MAX_RETRIES || true) {
                    // Only clean up pending on final attempt — handled below
                }
            }
        }
    })().finally(() => {
        if (isCacheable) {
            pendingRequests.delete(requestKey);
        }
    });

    if (isCacheable) {
        pendingRequests.set(requestKey, fetchPromise);
    }

    return fetchPromise;
}

export const api = {
    get: (endpoint, config) => fetcher(endpoint, { ...config, method: 'GET' }),
    post: (endpoint, body, config) => fetcher(endpoint, { ...config, method: 'POST', body }),
    put: (endpoint, body, config) => fetcher(endpoint, { ...config, method: 'PUT', body }),
    delete: (endpoint, config) => fetcher(endpoint, { ...config, method: 'DELETE' }),
};

